
































































import Vue from 'vue'
// Components
import SearchBar from '@/components/Global/Searchbar.vue'
import TripLines from '@/components/Map/Trips.vue'
import BottomControls from './BottomControls.vue'
import EntityCluster from 'vue2-google-maps/dist/components/cluster'
import InfoWindow from '@/components/Google/InfoWindow.vue'
import SelectedMarker from '@/components/Google/SelectedMarker/index.vue'
// Libs
import { mapState } from 'vuex'
import { Route } from 'vue-router'
import io from 'socket.io-client'
// Map
import { gmapApi } from 'vue2-google-maps'
import * as GoogleMaps from 'vue2-google-maps'
import GoogleConfig from '@/config/google'
import {} from 'googlemaps'
// Config
import socketConfig from '@/config/socket'
// Utils
import { decrypt } from '@/helpers/crypt'
import { getPos, setMap } from '@/components/Map/currentLocation'
import { clusterStyles } from '@/components/Map/clusters'
import { fitBounds } from '@/components/Map/bounds'
import { maybeSetMarkerFn } from '@/components/Map/route'
import { tracefyBlue } from '@/plugins/vuetify'
// Assets
import defaultMarker from '@/assets/markers/marker-default-md.png'
import selectedMarker from '@/assets/markers/marker-selected-md.png'
import activeMarker from '@/assets/markers/marker-moving-md.png'
import unregisteredMarker from '@/assets/markers/marker-unregistered-md.png'
import satellite from '@/assets/markers/satellite.png'
// State
import { MutationTypes } from '@/store/admin/Map/types'
import { Mutations } from '@/store/admin/Map/mutations'
import { State } from '@/store/admin/Map/state'
// FP
import * as E from 'fp-ts/lib/Either'
import { flow } from 'fp-ts/lib/function'
import { Optional } from 'monocle-ts'
import { Eq, getStructEq, eqNumber } from 'fp-ts/lib/Eq'
import * as O from 'fp-ts/lib/Option'
// Types
import { IMEI, Marker, Position, Trip } from '@/components/Map/types'
// Queries
import { nightmode } from '@/components/Map/styles'

const lang = JSON.parse(decrypt(localStorage.getItem('lang') || undefined)) || { key: 'en' }

const eqPosition: Eq<Position> = getStructEq({
  lat: eqNumber,
  lng: eqNumber
})

const eqMarker: Eq<Marker> = getStructEq({
  imei: eqNumber
})

const logger = (s: any): void => {
  // console.error(s)
}

Vue.use(GoogleMaps, {
  load: {
    key: GoogleConfig.apiKey,
    libraries: GoogleConfig.libraries,
    region: lang.key,
    language: lang.key
  }
})

export default Vue.extend({
  name: 'GoogleMapContainer',
  components: {
    InfoWindow,
    BottomControls,
    TripLines,
    EntityCluster,
    SelectedMarker,
    SearchBar,
  },
  data: () => ({
    info: {},
    currentPositionMarker: [],
    gridSize: 50,
    resetSearch: false,
    entity: {},
    markers: [],
    socket: null,
    activeMarkers: [],
    selectedMarkers: [],
    center: { lat: 52.03050336, lng: 5.278821665 },
    batchWorker: new Worker('/workers/update-batch.worker.js'),
    processWorker: new Worker('/workers/process-batch.worker.js'),
    satelliteImei: '999999999999999',
    styles: clusterStyles,
    tracefyBlue
  }),
  computed: {
    google: gmapApi,
    ...mapState(['user', 'locale']),
    ...mapState('Trips', ['trips']),
    ...mapState('Debug', { 'selectedListener': 'selected' }),
    ...mapState('Map', ['drawer', 'selectedIMEI', 'selectedMarker', 'search', 'markersLoaded']),
    selectedMarkersPositions (): Position[] {
      return this.selectedMarkers?.map((m: Marker) => m.position) || []
    },
    options () {
      if (this.$vuetify.theme.dark) {
        return { maxZoom: 20, minZoom: 3, styles: nightmode }
      } else {
        return { maxZoom: 20, minZoom: 3, styles: [] }
      }
    },
    shownMarkers (): Marker[] {
      return this.markers
        .filter(this.shouldShowMarker)
        .map((m: Marker) => ({
          ...m,
          active: this.activeMarkers.some((am: Marker) => am.imei === m.imei)
        }))
    },
    tripsPositions (): Position[] {
      return this.trips.flatMap((t: Trip) => t.path)
    }
  },
  created () {
    this.batchWorker.addEventListener('message', ({ data }: any) => {
      if (!data.hasOwnProperty('updates')) {
        return
      }

      this.processWorker.postMessage({
        updates: data.updates,
        items: this.markers
      })
    }, false)

    this.processWorker.addEventListener('message', ({ data }: any) => {
      if (data.hasOwnProperty('updates')) return
      data.forEach((update: any) => {
        // TODO: Update to state
        this.$set(this.markers, update.index, update)
        if (update.imei === this.selectedIMEI) {
          this.$store.commit(`Map/${MutationTypes.SET_MARKER}`, update)
        }
      })

      this.activeMarkers = this.activeMarkers
        .filter((m: any) => m.updated > (new Date().getTime() - 60000))
        .concat(data.map(this.createTimestampedMarker))
      console.log('markers processed.')
    }, false)
  },
  beforeDestroy () {
    this.batchWorker.terminate()
    this.processWorker.terminate()
  },
  async mounted () {
    this.loadMarkers()

    const token = await this.$auth.getTokenSilently()

    const createSocketConnection = () => {
      this.socket = io(`${socketConfig.address}/markers`, {
        transports: ['websocket'],
        query: { 'access_token': token, 'user': this.user.id }
      })
    }
    createSocketConnection()

    this.socket.on('update', (imei: IMEI, position: Position, positioned_at: any, margin: number) => this.batchWorker.postMessage({
      imei: imei,
      position,
      positioned_at,
      margin
    }))

    // this.socket.on('disconnect', createSocketConnection)

    this.$refs.MapRef.$mapPromise.then((map: google.maps.Map) => {
      map.addListener('click', () => {
        if (this.trips.length === 0) this.closeDrawer()
      }) // Fix in FSM
      map.addListener('zoom_changed', () => {
        const zoom = map.getZoom()

        if (this.selectedMarker?.position && this.trips.length === 0) {
          this.panToPosition(this.selectedMarker.position)
        }

        if (zoom === 20 && this.gridSize !== 5) {
          this.gridSize = 5
        } else if (zoom !== 20 && this.gridSize !== 50) {
          this.gridSize = 50
        }
      })
    })
  },
  destroyed (): Mutations<State> {
    this.closeDrawer()
    return this.$store.commit('Map/resetMap')
  },
  methods: {
    createTimestampedMarker (m: Marker): Record<string, string | number> {
      return {
        imei: m.imei,
        updated: new Date().getTime()
      }
    },
    markerIcon (m: Marker): string {
      if (m.imei === this.satelliteImei) {
        return satellite
      }
      return m.imei === this.selectedIMEI
        ? selectedMarker
        : this.movingOrDefaultMarker(m)
    },
    movingOrDefaultMarker (m: Marker): string {
      if (m.imei === this.satelliteImei) {
        return satellite
      }
      return m.status === 'UNREGISTERED'
        ? unregisteredMarker
        : m.active
          ? activeMarker
          : defaultMarker
    },
    shouldShowMarker (m: Marker): boolean {
      return !this.search.bounding || this.selectedMarkers.some((mm: Marker) => mm.imei === m.imei)
    },
    setMarker (marker: Marker): Mutations<State> {
      this.$store.commit(`Map/${MutationTypes.SET_SEARCH_BOUNDING}`, false)
      this.$store.commit(`Map/${MutationTypes.SET_IMEI}`, marker.imei)
      this.$store.commit(`Map/${MutationTypes.SET_MARKER}`, marker)
      return this.$store.commit(`Map/${MutationTypes.TOGGLE_DRAWER}`, true)
    },
    closeDrawer (): Mutations<State> {
      this.$store.commit(`Map/${MutationTypes.TOGGLE_DRAWER}`, false)
      this.$store.commit(`Map/${MutationTypes.SET_IMEI}`, null)
      this.$store.commit(`Map/${MutationTypes.SET_MARKER}`, null)
      this.resetSearchBar()
      return this.$store.commit('Trips/resetView')
    },
    resetSearchBar () {
      this.resetSearch = true
      setTimeout(() => { this.resetSearch = false }, 500)
    },
    reset () {
      this.$refs.MapRef.$mapPromise.then((map: google.maps.Map) => map.setZoom(4))
      this.panToPosition(this.center)
      this.closeDrawer()
      this.markers = []
      this.$store.commit('Map/resetMap')
      this.loadMarkers()
    },
    goToCurrentLocation (): void | Navigator {
      this.closeDrawer()
      navigator.geolocation?.getCurrentPosition(
        getPos(this, this.google, setMap),
        logger,
        { enableHighAccuracy: true }
      )
    },
    panToPosition (position: Position): Promise<google.maps.Map> {
      return this.$refs.MapRef.$mapPromise.then((map: google.maps.Map) => map.panTo(position))
    },
    setInfo (isToggled: boolean, i: IMEI): any {
      const marker = isToggled ? this.markers.find((m: Marker) => m.imei === i) : {}
      this.info = { ...marker }
    },
    showFleet () { // EffOption <VueEff<Marker>
      const px = this.markers.map((m: Marker) => m.position)
      this.fitBounds(px)
      this.selectedMarkers = []
      this.closeDrawer()
    },
    filterMarkers (filtered: Marker[]): void { // EffOption <VueEff<Marker>
      flow(
        O.fromNullable as ((m: Marker[]) => O.Option<Marker[]>),
        O.map((f: Marker[]) => {
          this.selectedMarkers = [...f]
        })
      )(filtered)
    },
    isValidMarker (marker: Marker): boolean {
      const conditions = [
        (m: Marker) => m?.imei !== null,
        (m: Marker) => m?.id !== null,
        (m: Marker) => m.position?.lat !== null,
        (m: Marker) => m.position?.lng !== null
      ]
      return conditions.reduce((b: boolean, c: Function) => b && c(marker), true)
    },
    fitBounds (px: Position[]) {
      fitBounds(this.google, this.$refs.MapRef)(px)
    }, // Eff :: google.maps.Map
    loadMarkers () {
      this.$store.commit('Map/TOGGLE_MARKERS_LOADING', false)

      this.axios.get('/entities/markers').then((response: any) => {
        this.markers = response.data.map((m: any) => {
          m.name = m.entity_name
          return { ...m }
        })
        this.$store.commit('Map/TOGGLE_MARKERS_LOADING', true)
      })
    },
    centerPosition (p: Position) { // Eff :: google.maps.Map
      this.fitBounds([p])
      this.panToPosition(p)
    }
  },
  watch: {
    // selectedMarker (m: Marker, mOld: Marker) {
    selectedMarker (m: Marker, mOld: Marker) {
      return flow(
        O.fromNullable as ((m: Marker) => O.Option<Marker>),
        O.map((m: Marker): E.Either<Position, Position> => mOld && eqMarker.equals(m, mOld)
          ? this.panToPosition(m.position)
          : this.centerPosition(m.position)
        )
      )(m)
    },
    selectedIMEI (imei: IMEI): void { // Eff<Either<google.maps.Map, Route>>
      flow(
        E.fromPredicate(
          (imei: IMEI) => String(imei) !== this.$route?.params?.imei,
          _ => 'Prevented redundant selection update.'
        ),
        E.map(flow(
          (imei: IMEI) => imei !== null && imei !== undefined ? String(imei) : '',
          (s: string) => {
            this.$router.push(`/${this.$route.name}${s.length > 0 ? `/${s}` : ''}`)
            return s
          },
          (s: string) => {
            this.selectedMarkers = this.markers.filter((m: Marker) => String(m.imei) === s)
          }
        )),
        E.mapLeft(logger)
      )(imei)
    },
    tripsPositions (): void {
      type Trip = [{lat: number; lng: number}]
      if (this.trips.length < 1) return
      const waypoints = this.trips.map((t: { path: Trip }) => t.path)
      const totalWaypoints: any = []
      waypoints.forEach((trip: Trip) => totalWaypoints.push(...trip))
      const bounds = new this.google.maps.LatLngBounds()
      totalWaypoints.forEach((point: { lat: number; lng: number }) => bounds.extend(point))
      this.$refs.MapRef.fitBounds(bounds)
    },
    selectedMarkersPositions (ps: Position[]): E.Either<any, google.maps.Map> {
      const hasPositions = E.fromPredicate(
        ([...px]: any[]) => px?.length > 0,
        _ => 'No positions found'
      )

      return flow(
        hasPositions,
        E.map(px => this.fitBounds(px)),
        E.mapLeft(logger)
      )(ps)
    },
    locale (): void {
      this.$router.go()
    }, // Eff<Route>
    // TODO: Refactor both?
    markersLoaded (loaded: boolean): O.Option<E.Either<Mutations<State>, Console>> {
      const routeExists = (r: Route): E.Either<string, Route> => r ? E.right(r) : E.left('Route not available')
      const pathLens = Optional.fromPath<Route>()(['params', 'imei'])

      return flow(
        (r: Route) => loaded ? O.some(r) : O.none,
        O.map(flow(
          pathLens.getOption,
          O.fold(
            this.showFleet,
            () => maybeSetMarkerFn(routeExists, this.$route, this.selectedIMEI, this.markers, this.setMarker, logger)
          ))
        )
      )(this.$route)
    },
    $route (to): E.Either<Mutations<State>, Console> {
      const areMarkersLoaded = (to: Route) => this.markersLoaded ? E.left('Markers are not loaded.') : E.right(to)
      return maybeSetMarkerFn(areMarkersLoaded, to, this.selectedIMEI, this.markers, this.setMarker, logger)
    }
  }
})

